<template>
  <div ref="dropdown" :class="['category-dropdown', { active: isActive }]">
    <div class="category-dropdown__item-wrp" @click="toggleActivation">
      <img
        v-if="!currentItem"
        class="category-dropdown__icon"
        :src="`/img/power-calculator/${dataItem.icon}.svg`"
        :alt="_T(dataItem.altImg)"
      />
      <img
        v-else
        class="category-dropdown__icon"
        :src="`/img/power-calculator/${dataItem.icon}-active.svg`"
        :alt="_T(dataItem.altImg)"
      />
      <span class="category-dropdown__title">{{ _T(dataItem.title) }}</span>
    </div>

    <div
      v-if="getSelectedCount && dataItem.list"
      :class="[
        'category-dropdown__selected-list-item',
        { active: isDropdownOpen },
      ]"
      @click.stop="isDropdownOpen = !isDropdownOpen"
    >
      <span class="category-dropdown__selected-amount">{{
        getSelectedCount
      }}</span>
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 6L8 10L4 6"
          stroke="#F36C21"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </svg>
    </div>
    <ul v-if="dataItem.list && isDropdownOpen" class="category-dropdown__list">
      <li
        v-for="(item, index) in dataItem.list"
        :key="index"
        :class="{ active: getSelectedCount === item }"
        @click.stop="selectItem(item)"
      >
        {{ item }}
      </li>
    </ul>
  </div>
</template>

<script setup>
import { usePowerCalcStore } from "~/store/powerCalcStore";
import { addScopedEventListener } from "~~/utils/eventListener";

const powerCalculatorStore = usePowerCalcStore();

const emit = defineEmits(["updateSelectedCategory"]);

const props = defineProps({
  dataItem: { type: Object, required: true },
  category: { type: String, required: false },
});

const allSelectedItems = computed(
  () => powerCalculatorStore.getAllSelectedItems,
);
const getSelectedCount = computed(
  () =>
    powerCalculatorStore.getAllSelectedItems.filter(
      (item) => props.dataItem.itemName === item.itemName,
    ).length,
);

const currentItem = computed(() => {
  return (
    allSelectedItems.value.find(
      (item) => item.itemName === props.dataItem.itemName,
    ) || null
  );
});

const isActive = computed(() => !!currentItem.value);

const isDropdownOpen = ref(false);
const selectedItem = ref(null);
const dropdown = ref(null);

function toggleActivation() {
  const newState = !isActive.value;
  const item = {
    ...props.dataItem,
    startingPowerDemand: props.dataItem.startingPowerDemand
      ? props.dataItem.startingPowerDemand
      : false,
  };
  powerCalculatorStore.setItems(props.category, item, newState ? 1 : 0);

  if (!newState) {
    selectedItem.value = 1;
  }
}

function selectItem(item) {
  selectedItem.value = item;
  isDropdownOpen.value = false;
  const newItem = {
    ...props.dataItem,
    startingPowerDemand: props.dataItem.startingPowerDemand
      ? props.dataItem.startingPowerDemand
      : false,
  };

  powerCalculatorStore.setItems(props.category, newItem, selectedItem.value);
}

function handleClickOutside(event) {
  if (dropdown.value && !dropdown.value.contains(event.target)) {
    isDropdownOpen.value = false;
  }
}

onMounted(() => {
  addScopedEventListener(window, "click", handleClickOutside);
});
</script>

<style lang="scss" scoped>
.category-dropdown {
  width: max-content;

  @include flex-container(row, space-between, center);

  position: relative;

  background-color: var(--color-sky-lightest);

  border-radius: 30px;
  border: 1px solid transparent;

  gap: 8px;

  cursor: pointer;
  transition: border-color 0.1s ease-in-out;

  &:hover {
    border-color: var(--color-sky-base);
  }

  &.active {
    border-color: var(--color-primary-base);

    .category-dropdown {
      &__title {
        color: var(--color-primary-base);
      }
    }
  }

  @include bigMobile() {
    gap: 0;
  }

  &__item-wrp {
    @include flex-container(row, flex-start, center);

    gap: 8px;
    padding: 16px;

    @include bigMobile() {
      padding: 8px 16px;
    }
  }

  &__icon {
    @include fixedHW(24px);

    @include bigMobile() {
      @include fixedHW(22px);
    }
  }

  &__title {
    @include font(16, 22, 500);
    color: var(--color-grey-900);

    @include bigMobile() {
      @include font(14, 18, 500);
    }
  }

  &__selected-list-item {
    @include flex-container(row, flex-start, center);

    gap: 8px;
    padding: 16px 16px 16px 0;

    @include bigMobile() {
      gap: 4px;
      padding: 8px 16px 8px 0;
    }

    &.active {
      svg {
        transform: rotateZ(180deg);
      }
    }

    svg {
      transition: transform 0.3s ease-in-out;
    }
  }

  &__selected-amount {
    @include font(16, 22, 500);
    color: var(--color-primary-base);
  }

  &__list {
    width: 100%;

    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 10;

    @include flex-container(column);

    background-color: white;

    border: 1px solid var(--color-sky-light);
    border-radius: 24px;

    overflow: hidden;

    li {
      width: 100%;

      padding: 16px;

      &.active {
        background-color: var(--color-primary-lightest);
      }
    }
  }
}
</style>
